import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Table, Badge } from "react-bootstrap";
import { mapCaseForEmit, normalizeToMMDDYYYY } from "./CasePickerPanel";

const ROW_ACTIVE = "#CFE2FF";
const ROW_INACTIVE = "#f8f9fa";

const normId = (c) => String(c?._id?.$oid || c?._id || c?.id || "");

const toArray = (x) => {
  if (Array.isArray(x)) return x;
  if (Array.isArray(x?.cases)) return x.cases;
  if (Array.isArray(x?.data)) return x.data;
  if (Array.isArray(x?.items)) return x.items;
  return [];
};

const parseEndDate = (mmddyyyy) => {
  if (!mmddyyyy) return null;
  const [mm, dd, yyyy] = mmddyyyy.split("-");
  if (!mm || !dd || !yyyy) return null;
  const d = new Date(`${yyyy}-${mm}-${dd}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const daysUntil = (endDate) => {
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ms = endDate.getTime() - today.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

export default function SoonExpiringCasePrompt({
  show,
  onHide,
  cases = [],
  onUseCase,
  onAddNew,
}) {
  const [selectedCaseId, setSelectedCaseId] = useState("");

  useEffect(() => {
    if (!show) {
      setSelectedCaseId("");
    }
  }, [show]);

  const sortedCases = useMemo(() => {
    const arr = [...toArray(cases)];
    const endKey = (c) => {
      const end = normalizeToMMDDYYYY(c?.caseEndDate);
      if (!end) return "99999999"; // no end date floats to top
      const [mm, dd, yyyy] = end.split("-");
      return `${yyyy}${mm}${dd}`;
    };
    arr.sort((a, b) => endKey(b).localeCompare(endKey(a)));
    return arr;
  }, [cases]);

  useEffect(() => {
    if (show && sortedCases.length) {
      setSelectedCaseId(normId(sortedCases[0]));
    }
  }, [show, sortedCases]);

  const selectedCase = useMemo(
    () => sortedCases.find((c) => normId(c) === selectedCaseId) || null,
    [sortedCases, selectedCaseId]
  );

  const handleUse = () => {
    if (!selectedCase) return;
    onUseCase?.(mapCaseForEmit(selectedCase));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <h6 className="text-primary mt-1 fw-normal m-0">PATIENT CASES</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">
            This patient has active or soon-expiring cases. Pick one to attach,
            or add a new case.
          </div>
        </div>

        {!sortedCases.length ? (
          <div className="text-muted">No relevant cases.</div>
        ) : (
          <div className="rounded p-2">
            <Table hover size="sm" responsive className="mb-0">
              <tbody className="border">
                {sortedCases.map((c) => {
                  const id = normId(c);
                  const isActiveRow = id === selectedCaseId;
                  const start = normalizeToMMDDYYYY(c?.caseStartDate);
                  const end = normalizeToMMDDYYYY(c?.caseEndDate);
                  const days = daysUntil(parseEndDate(end));
                  const secondaries = toArray(c?.icd102);

                  return (
                    <tr
                      key={id}
                      onClick={() => setSelectedCaseId(id)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isActiveRow
                          ? ROW_ACTIVE
                          : ROW_INACTIVE,
                      }}
                    >
                      <td
                        style={{
                          backgroundColor: isActiveRow
                            ? ROW_ACTIVE
                            : ROW_INACTIVE,
                        }}
                      >
                        <div className="fw-semibold text-primary">
                          {c?.caseDescription || "Untitled Case"}
                          {days !== null && days >= 0 && days <= 30 && (
                            <Badge
                              bg={days <= 7 ? "danger" : "warning"}
                              text={days <= 7 ? "light" : "dark"}
                              className="ms-2"
                            >
                              Ending in {days} day{days === 1 ? "" : "s"}
                            </Badge>
                          )}
                          {days !== null && days < 0 && days >= -30 && (
                            <Badge bg="dark" className="ms-2">
                              Ended {Math.abs(days)} day
                              {Math.abs(days) === 1 ? "" : "s"} ago
                            </Badge>
                          )}
                          {!end && (
                            <Badge bg="secondary" className="ms-2">
                              No end date
                            </Badge>
                          )}
                        </div>
                        <div className="small text-muted">
                          {start || "-"} {end ? `→ ${end}` : ""}
                        </div>
                        <div className="small text-muted">
                          Ref: {c?.referringDoctorName || "-"} | BHF{" "}
                          {c?.referringDoctorBHF || "-"} | HPCSA{" "}
                          {c?.referringDoctorHPCSA || "-"}
                        </div>
                        <div className="mt-1">
                          {c?.icd101 && (
                            <Badge bg="secondary" className="me-1">
                              {c.icd101}
                            </Badge>
                          )}
                          {secondaries.slice(0, 2).map((s, i) => (
                            <Badge
                              key={`${id}-sec-${i}`}
                              bg="light"
                              text="dark"
                              className="me-1"
                            >
                              {s}
                            </Badge>
                          ))}
                          {secondaries.length > 2 && (
                            <span className="text-muted">
                              +{secondaries.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Dismiss
        </Button>
        <Button variant="outline-primary" size="sm" onClick={onAddNew}>
          + New Case
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleUse}
          disabled={!selectedCase}
        >
          Use selected case
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
